/**
 * OI Historical Backfill service (admin-only) — talks to
 * `/api/v1/oi-backfill/*` (see `app.api.v1.oi_backfill`). The entire router
 * requires ADMIN role server-side; every method here will 403 for a
 * non-admin caller regardless of what the UI shows.
 *
 * Note on error shapes: job-not-found and invalid-state-transition (e.g.
 * pausing a job that isn't RUNNING) both come back as HTTP 422 with a
 * human-readable `message` (see `app.api.v1.oi_backfill`'s docstring on
 * ERROR SEMANTICS) — not 404. `extractErrorMessage` already surfaces
 * `message` directly, so no special-casing is needed here.
 */

import api from "@/services/api";
import type {
  BackfillJobCreateRequest,
  BackfillJobProgressResponse,
  BackfillJobRead,
  BackfillJobStatus,
  BackfillWorkItemRead,
  BackfillWorkItemStatus,
  OptionMinutePreviewRequest,
  OptionMinutePreviewResponse,
} from "@/types";

export interface ListBackfillJobsParams {
  status?: BackfillJobStatus;
  limit?: number;
}

export interface ListBackfillWorkItemsParams {
  status?: BackfillWorkItemStatus;
}

export const oiBackfillService = {
  /** Dry run of an "Option prices (1-minute)" job: contracts and Breeze calls per day; nothing is fetched. */
  async previewOptionMinute(body: OptionMinutePreviewRequest): Promise<OptionMinutePreviewResponse> {
    const { data } = await api.post<OptionMinutePreviewResponse>("/oi-backfill/option-minute/preview", body);
    return data;
  },

  async createJob(body: BackfillJobCreateRequest): Promise<BackfillJobRead> {
    const { data } = await api.post<BackfillJobRead>("/oi-backfill", body);
    return data;
  },

  async listJobs(params: ListBackfillJobsParams = {}): Promise<BackfillJobRead[]> {
    const { data } = await api.get<BackfillJobRead[]>("/oi-backfill", { params });
    return data;
  },

  async getJobProgress(jobId: string): Promise<BackfillJobProgressResponse> {
    const { data } = await api.get<BackfillJobProgressResponse>(`/oi-backfill/${jobId}`);
    return data;
  },

  async listWorkItems(
    jobId: string,
    params: ListBackfillWorkItemsParams = {}
  ): Promise<BackfillWorkItemRead[]> {
    const { data } = await api.get<BackfillWorkItemRead[]>(`/oi-backfill/${jobId}/work-items`, {
      params,
    });
    return data;
  },

  async pauseJob(jobId: string): Promise<BackfillJobRead> {
    const { data } = await api.post<BackfillJobRead>(`/oi-backfill/${jobId}/pause`);
    return data;
  },

  async resumeJob(jobId: string): Promise<BackfillJobRead> {
    const { data } = await api.post<BackfillJobRead>(`/oi-backfill/${jobId}/resume`);
    return data;
  },

  async cancelJob(jobId: string): Promise<BackfillJobRead> {
    const { data } = await api.post<BackfillJobRead>(`/oi-backfill/${jobId}/cancel`);
    return data;
  },
};

export default oiBackfillService;
